/**
 * Uygulama metinleri (Türkçe). Kapsamlı i18n kütüphanesi kurulmaz (WP 2.7 K-8); metinler ileride çevrilebilsin diye
 * bileşenlere dağıtılmaz, buradan okunur. Kurallar (docs/design/README.md §Metin):
 * cümle düzeninde başlık · fiil odaklı düğme · aynı işlem her yerde aynı ad · teknik hata metni kullanıcıya gösterilmez ·
 * geri alınamaz eylemde nesnenin açık adı · boş durumda anlamlı sonraki eylem · gerçekleşmemiş başarı bildirilmez.
 */
import type { Module, UserRole } from '@castinflow/shared';
import { en } from './en';
import { mergeDictionary, storedUiLanguage } from './language';

export const ROLE_LABEL: Record<UserRole, string> = {
  system_admin: 'Sistem yöneticisi',
  standard: 'Standart',
  limited: 'Sınırlı',
  fully_limited: 'Tamamen sınırlı',
  // G3 (sahip kararı D): görünen ad "Temel CRM"; rol kimliği `crm_lite`, ücret ve haklar DEĞİŞMEZ
  crm_lite: 'Temel CRM',
};

export const USER_STATUS_LABEL = {
  invited: 'Davet edildi',
  active: 'Etkin',
  suspended: 'Askıya alındı',
  left: 'Ayrıldı',
} as const;

export const MODULE_LABEL: Record<Module, string> = {
  newsfeed: 'Haber akışı',
  leads: 'Müşteri adayları',
  opportunities: 'Fırsatlar',
  proposals: 'Teklifler',
  orders: 'Siparişler',
  contracts: 'Sözleşmeler',
  activities: 'Aktiviteler',
  tickets: 'Destek talepleri',
  forms: 'Formlar',
  contacts: 'Kişiler',
  companies: 'Şirketler',
  products: 'Ürünler',
};

const trBase = {
  appName: 'Castinflow',
  pageTitle: (title: string) => `${title} · Castinflow`,

  nav: {
    overview: 'Genel bakış',
    // v3 C1
    today: 'Bugün',
    // v3.1 S10
    inbox: 'Gelen kutusu',
    channels: 'İletişim kanalları',
    quickReplies: 'Hazır yanıtlar',
    feed: 'Haber akışı',
    companies: 'Şirketler',
    contacts: 'Kişiler',
    products: 'Ürünler',
    transfers: 'Aktarımlar',
    sales: 'Satış',
    // v3.9 ana alanlar (Bugün = today, Satış = sales, Yönetim = administration)
    areaCrm: 'CRM',
    areaComms: 'İletişim',
    areaMarketing: 'Pazarlama',
    areaWork: 'İşler',
    areaReports: 'Raporlar',
    leads: 'Müşteri adayları',
    contracts: 'Sözleşmeler',
    opportunities: 'Fırsatlar',
    opportunityBoard: 'Fırsat panosu',
    proposals: 'Teklifler',
    orders: 'Siparişler',
    purchaseOrders: 'Tedarikçi siparişleri',
    suppliers: 'Tedarikçiler',
    approvals: 'Onay bekleyenler',
    administration: 'Yönetim',
    // v3.9 Yönetim merkezi kategorileri
    adminOrg: 'Kuruluş ve ekip',
    adminRules: 'Özelleştirme ve iş kuralları',
    adminConnections: 'Bağlantılar ve iletişim',
    adminPlanUsage: 'Plan ve kullanım',
    adminData: 'Veri, güvenlik ve hesap',
    users: 'Kullanıcılar',
    settings: 'Ayarlar',
    workCalendar: 'Çalışma takvimi',
    publicLinks: 'Web formları ve bağlantılar',
    automation: 'Otomasyon',
    email: 'E-posta',
    inboundEmail: 'Gelen e-posta',
    leadOverview: 'Aday panosu',
    leadSequences: 'Aday sekansları',
    orderBoard: 'Sipariş kuşbakışı',
    // M4 WP 4.2
    activities: 'Aktiviteler',
    activityBoard: 'Aktivite kuşbakışı',
    activityCalendar: 'Takvim',
    activityMap: 'Aktivite haritası',
    performance: 'Performans',
    dashboard: 'Performans monitörü',
    goals: 'Hedefler',
    activityReport: 'Faaliyet raporu',
    // M4 WP 4.4
    tickets: 'Destek talepleri',
    supportOverview: 'Destek panosu',
    // M5 WP 5.1
    forms: 'Formlar',
    formDocuments: 'Form belgeleri',
    // M5 WP 5.3
    gamification: 'Puan ve rozetler',
    gamificationSettings: 'Oyunlaştırma',
  },

  shell: {
    soon: 'Yakında',
    searchSoon: 'Arama',
    searchSoonHint: 'Genel arama yakında kullanılabilir olacak.',
    notificationsSoon: 'Bildirimler (yakında)',
    userMenu: (name: string) => `Kullanıcı menüsü: ${name}`,
    workspace: 'Çalışma alanı',
    systemStatus: 'Sistem durumu',
    logout: 'Çıkış yap',
    checkingSession: 'Oturum kontrol ediliyor',
  },

  states: {
    notFoundTitle: 'Sayfa bulunamadı',
    notFoundHeading: 'Bu adreste bir sayfa yok',
    notFoundBody: 'Adres yanlış yazılmış ya da sayfa kaldırılmış olabilir.',
    backHome: 'Genel bakışa dön',
    unauthorizedTitle: 'Bu sayfayı görüntüleme yetkiniz yok',
    unauthorizedBody:
      'Erişim için çalışma alanı yöneticinizden gerekli yetkiyi isteyin. Yetkiniz varsa sayfayı yenileyin.',
    systemErrorTitle: 'Beklenmeyen bir sorun oluştu',
    systemErrorBody:
      'Sayfayı yenileyerek yeniden deneyin. Sorun sürerse sistem durumunu kontrol edin.',
    reload: 'Sayfayı yenile',
    loadErrorTitle: 'Bilgiler yüklenemedi',
    loadErrorBody: 'Bağlantınızı kontrol edip yeniden deneyin.',
    retry: 'Yeniden dene',
    sessionExpiredTitle: 'Oturumunuz sona erdi',
    sessionExpiredBody: 'Devam etmek için yeniden giriş yapın.',
    goToLogin: 'Giriş yap',
  },

  auth: {
    loginTitle: 'Giriş yap',
    loginIntro: 'Çalışma alanı adresiniz ve hesap bilgilerinizle giriş yapın.',
    workspaceAddress: 'Çalışma alanı adresi',
    email: 'E-posta',
    password: 'Parola',
    submitLogin: 'Giriş yap',
    noAccount: 'Hesabınız yok mu?',
    createAccount: 'Hesap oluşturun',
    haveAccount: 'Hesabınız var mı?',
    goLogin: 'Giriş yapın',
    totpTitle: 'İki adımlı doğrulama',
    totpCode: 'Doğrulama kodu (6 hane)',
    recoveryCode: 'Kurtarma kodu',
    verify: 'Doğrula',
    useRecovery: 'Kurtarma kodu kullan',
    useTotp: 'Doğrulama kodu kullan',
    rateLimited: 'Çok fazla deneme yapıldı. Birkaç dakika sonra yeniden deneyin.',
    invalidCredentials: 'Bilgiler doğrulanamadı. Adresi, e-postayı ve parolayı kontrol edin.',
    genericError: 'Giriş şu anda tamamlanamadı. Biraz sonra yeniden deneyin.',
    // M6 WP 6.2: passkey ile giriş ve IP izin listesi
    passkeyLogin: 'Passkey ile giriş yap',
    passkeyNeedsWorkspace: 'Passkey ile giriş için önce çalışma alanı adresini yazın.',
    passkeyUnsupported: 'Bu tarayıcı passkey desteklemiyor.',
    passkeyFailed:
      'Passkey ile giriş tamamlanamadı. Yeniden deneyin ya da parolanızla giriş yapın.',
    ipNotAllowed:
      'Bu çalışma alanına bağlandığınız ağdan erişime izin verilmiyor. Çalışma alanı yöneticinize başvurun.',
    or: 'ya da',
    signupTitle: 'Yeni hesap',
    signupIntro:
      'Şirketiniz için bir çalışma alanı oluşturun; ilk kullanıcı olarak yönetici olursunuz.',
    companyName: 'Şirket adı',
    workspaceKind: 'Çalışma alanı türü',
    workspaceReal: 'Gerçek çalışma alanı',
    workspaceRealHint: 'Boş başlar; kendi müşteri ve belgelerinizi girersiniz.',
    workspaceDemo: 'Demo çalışma alanı',
    workspaceDemoHint:
      'Denemek için ayrı bir alan; hayali örnek verilerle doldurulabilir. E-posta ve davet gönderilmez; sonradan gerçeğe dönüştürülmez.',
    firstName: 'Ad',
    lastName: 'Soyad',
    passwordHint: 'En az 10 karakter',
    passwordLabel: 'Parola',
    addressPreview: 'Adres',
    addressAvailable: 'uygun',
    addressTaken: 'kullanımda',
    submitSignup: 'Hesap oluştur',
    signupConflict:
      'Bu çalışma alanı adresi zaten kullanılıyor. Şirket adını değiştirerek yeniden deneyin.',
    signupError: 'Hesap şu anda oluşturulamadı. Biraz sonra yeniden deneyin.',
    signupDoneTitle: 'Hesap oluşturuldu',
    signupDoneBody: 'Çalışma alanı adresiniz aşağıdadır. Bu adresle giriş yapabilirsiniz.',
    // M7 T1: kayıt kapısı (durum sunucudan okunur; kapalıyken form gösterilmez)
    signupClosedTitle: 'Kayıt kapalı',
    signupClosedBody:
      'Yeni çalışma alanı kaydı şu anda kapalı. Çalışma alanları Castinflow tarafından açılır; çalışma alanınız varsa giriş yapabilirsiniz.',
    signupStatusLoading: 'Kayıt durumu denetleniyor',
    signupStatusErrorTitle: 'Kayıt durumu okunamadı',
    signupStatusErrorBody: 'Kayıt sayfası şu anda açılamadı. Biraz sonra yeniden deneyin.',
    signupStatusRetry: 'Yeniden dene',
    referenceCode: 'Destek için başvuru kodu',
    // M7 T2: e-postadaki bağlantıyla açılan sayfalar (davet kabulü, parola sıfırlama) ve "parolamı unuttum"
    forgotLink: 'Parolamı unuttum',
    backToLogin: 'Giriş sayfasına dön',
    newPassword: 'Parola',
    newPasswordRepeat: 'Parola (tekrar)',
    newPasswordMismatch: 'Parola ile tekrarı aynı değil.',
    linkError: 'İşlem şu anda tamamlanamadı. Biraz sonra yeniden deneyin.',
    inviteTitle: 'Daveti kabul et',
    inviteIntro:
      'Hesabınız için bir parola belirleyin. Parolayı belirlediğinizde çalışma alanına giriş yapılır.',
    inviteSubmit: 'Parolayı belirle ve giriş yap',
    inviteSessionNote:
      'Bu tarayıcıda açık bir oturum var. Daveti kabul ettiğinizde bu tarayıcıda yeni hesabınızla oturum açılır.',
    inviteInvalidTitle: 'Davet bağlantısı kullanılamıyor',
    inviteInvalidBody:
      'Bağlantı geçersiz, süresi dolmuş ya da daha önce kullanılmış olabilir. Çalışma alanı yöneticinizden daveti yeniden göndermesini isteyin.',
    inviteMissingTitle: 'Davet bağlantısı eksik',
    inviteMissingBody:
      'Bu sayfa e-postadaki davet bağlantısıyla açılır. Bağlantıyı e-postadan yeniden açın; sayfayı yenilediyseniz bağlantıya yeniden tıklamanız gerekir.',
    resetTitle: 'Yeni parola belirle',
    resetIntro:
      'Hesabınız için yeni bir parola belirleyin. Parola değişince açık oturumlarınız kapatılır.',
    resetSubmit: 'Parolayı değiştir',
    resetDoneTitle: 'Parolanız değiştirildi',
    resetDoneBody: 'Açık oturumlarınız kapatıldı. Yeni parolanızla giriş yapabilirsiniz.',
    resetInvalidTitle: 'Sıfırlama bağlantısı kullanılamıyor',
    resetInvalidBody:
      'Bağlantı geçersiz, süresi dolmuş ya da daha önce kullanılmış olabilir. Giriş sayfasındaki “Parolamı unuttum” ile yeni bir bağlantı isteyin.',
    resetMissingTitle: 'Sıfırlama bağlantısı eksik',
    resetMissingBody:
      'Bu sayfa e-postadaki sıfırlama bağlantısıyla açılır. Bağlantıyı e-postadan yeniden açın; sayfayı yenilediyseniz bağlantıya yeniden tıklamanız gerekir.',
    forgotTitle: 'Parolamı unuttum',
    forgotIntro:
      'Çalışma alanı adresinizi ve e-posta adresinizi yazın. Sıfırlama bağlantısı e-postayla gönderilir.',
    forgotSubmit: 'Sıfırlama bağlantısı iste',
    forgotInvalid: 'Çalışma alanı adresini ve geçerli bir e-posta adresi yazın.',
    forgotDoneTitle: 'İstek alındı',
    /** Hesabın var olup olmadığına göre DEĞİŞMEYEN tek yanıt metni */
    forgotDoneBody:
      'Bu bilgilerle eşleşen etkin bir hesap varsa sıfırlama bağlantısı e-posta adresine gönderilir. İleti birkaç dakika içinde gelmezse gereksiz posta klasörünü denetleyin ya da çalışma alanı yöneticinize başvurun.',
    forgotAgain: 'Başka bir adres için iste',
    forgotMailOffTitle: 'Sıfırlama iletisi gönderilemiyor',
    forgotMailOffBody:
      'Bu kurulumda sistem e-postası yapılandırılmamış; sıfırlama bağlantısı gönderilemez. Parolanızı sıfırlamak için çalışma alanı yöneticinize ya da hizmet sağlayıcınıza başvurun.',
    forgotMailSinkTitle: 'Sınama kipi',
    forgotMailSinkBody: 'Bu kurulum sınama kipinde çalışıyor: iletiler gerçek alıcıya gönderilmez.',
  },

  home: {
    title: 'Genel bakış',
    welcome: (name: string) => `Hoş geldiniz, ${name}`,
    description: 'Çalışma alanınızın ve hesabınızın özeti.',
    workspace: 'Çalışma alanı',
    address: 'Adres',
    role: 'Rol',
    twoFactor: 'İki adımlı doğrulama',
    twoFactorOn: 'Etkin',
    twoFactorOff: 'Kapalı',
    nextTitle: 'Sıradaki adımlar',
    nextUsers: 'Ekibinizi davet edin ve departmanları düzenleyin.',
    nextSettings: 'Modül alanlarını ve yayın tercihlerini ayarlayın.',
    goUsers: 'Kullanıcılara git',
    goSettings: 'Ayarlara git',
  },

  org: {
    title: 'Kullanıcılar',
    description: 'Ekibinizi davet edin, rollerini ve departmanlarını düzenleyin.',
    departments: 'Departmanlar',
    departmentsEmptyTitle: 'Henüz departman yok',
    departmentsEmptyBody:
      'Kullanıcıları gruplamak ve kayıt paylaşımını düzenlemek için bir departman ekleyin.',
    newDepartment: 'Yeni departman adı',
    addDepartment: 'Departman ekle',
    departmentAdded: (name: string) => `“${name}” departmanı eklendi`,
    departmentFailed: 'Departman eklenemedi. Adı kontrol edip yeniden deneyin.',
    /** Departman ayarları: yeni kayıtların varsayılan paylaşımı + teklif onay politikası (ÜK-2; API /departments/:id/shares) */
    deptSettings: {
      open: 'Paylaşım ve onay',
      openFor: (name: string) => `${name}: paylaşım ve onay ayarları`,
      title: (name: string) => `${name} — paylaşım ve onay`,
      sharingTitle: 'Yeni kayıtların varsayılan paylaşımı',
      sharingHint:
        'Bu departmandaki kullanıcıların yeni oluşturduğu kayıtlara uygulanır; mevcut kayıtlar değişmez. Kaydın sahibi ve sistem yöneticileri her zaman erişir. Departmanı olmayan kullanıcının kayıtlarına varsayılan paylaşım uygulanmaz. Sınırlı rollü kullanıcılar yalnız kendi departman ağaçlarındaki ya da kendilerine doğrudan paylaşılan kayıtları görür; "Tüm çalışma alanı" onlara erişim açmaz.',
      colType: 'Kayıt türü',
      colReaders: 'Görebilenler',
      colWriters: 'Düzenleyebilenler',
      readersFor: (type: string) => `${type}: görebilenler`,
      writersFor: (type: string) => `${type}: düzenleyebilenler`,
      shareNone: 'Yalnız sahibi',
      shareDept: 'Bu departman (alt departmanlar dâhil)',
      shareCompany: 'Tüm çalışma alanı',
      shareCustom: (n: number) => `Özel (${String(n)} hedef; korunur)`,
      approvalTitle: 'Teklif onayı',
      approvalHint:
        'Onay, teklifin kayıttaki sahip departmanının politikasıyla belirlenir: teklif açılırken (ya da yetki sahibi değişirken) sahibin departmanı. Sahibi departmansızken açılan teklife politika uygulanmaz. Talep eden ve teklif sahibi onaylayamaz.',
      modeLegend: 'Onay kuralı',
      modeNone: 'Onay gerekmez',
      modeNoneHint: 'Teklifler onaya gönderilmeden finalleştirilebilir.',
      modeAlways: 'Her teklif onaya gider',
      modeAlwaysHint: 'Bu departmanın bütün teklifleri finalleştirilmeden önce onaylanmalıdır.',
      modeThreshold: 'Eşiklere göre',
      modeThresholdHint: 'Aşağıdaki koşullardan biri tutarsa onay gerekir.',
      amountThreshold: 'Tutar eşiği',
      amountThresholdHint: 'KDV hariç, indirimli tutar bu değere eşit veya üstündeyse',
      minAmount: 'En düşük tutar',
      minAmountHint: 'KDV hariç, indirimli tutar bu değerin altındaysa',
      discountThresholdPct: 'İndirim onay eşiği (%)',
      discountThresholdPctHint: 'Toplam ya da en yüksek satır indirimi bu oranı aşarsa',
      maxDiscountPct: 'İzin verilen en yüksek indirim (%)',
      maxDiscountPctHint: 'Toplam ya da en yüksek satır indirimi bu oranı aşarsa',
      minMarginPct: 'En düşük kâr marjı (%)',
      minMarginPctHint: 'Marj bu oranın altındaysa; maliyeti olmayan satırda onay istenir',
      stockCheck: 'Stok yetersizse onay iste',
      stockCheckHint: 'Stok takipli üründe kullanılabilir stok yetmezse',
      currencyNote:
        'Tutar eşikleri çalışma alanı para birimindedir; başka para birimindeki teklif her zaman onaya gider.',
      approverDept: 'Onaylayan departman',
      approverDeptHint: 'Bu departmanın üyeleri ve sistem yöneticileri onaylayabilir.',
      approverAdminsOnly: 'Yalnız sistem yöneticileri',
      invalidNumber: 'Geçerli, negatif olmayan bir sayı girin',
      invalidPercent: 'Oran 0 ile 100 arasında olmalı',
      thresholdNeedsRule: 'Eşiklere göre onay için en az bir koşul girin.',
      notSaved: 'Kaydedilmedi: işaretli alanları düzeltin.',
      save: 'Kaydet',
      cancel: 'Vazgeç',
      saved: 'Departman ayarları kaydedildi',
      saveFailed: 'Ayarlar kaydedilemedi. Değerleri kontrol edip yeniden deneyin.',
      forbidden: 'Departman ayarlarını değiştirme yetkiniz yok.',
    },
    usersTable: 'Kullanıcı listesi',
    colName: 'Ad',
    colEmail: 'E-posta',
    colRole: 'Rol',
    colStatus: 'Durum',
    colDepartment: 'Departman',
    ownerBadge: 'Sahip',
    noDepartment: 'Departman yok',
    departmentFor: (name: string) => `Departman: ${name}`,
    departmentChanged: 'Departman güncellendi',
    departmentChangeFailed: 'Departman güncellenemedi. Yeniden deneyin.',
    usersEmptyTitle: 'Henüz kullanıcı yok',
    usersEmptyBody: 'Ekibinizi aşağıdaki formla davet edin.',
    inviteTitle: 'Kullanıcı davet et',
    inviteDescription:
      'Davet bağlantısı sistem e-postasıyla gönderilir; iletinin durumu kullanıcı listesinde görünür.',
    // Staging kabul turu F14: demo alanında davet kapalı; form bunu baştan söyler, ret iletisi genel "bilgileri kontrol edin" olmaz
    inviteDemoNote:
      'Demo çalışma alanında kullanıcı daveti kapalıdır; davet e-postası gönderilmez.',
    inviteDemoRefused: 'Demo çalışma alanında davet gönderilmez (e-posta kapalıdır).',
    inviteSend: 'Davet et',
    // M7 T2: davet kaydı oluştu; iletinin gönderildiği iddia edilmez (durum aşağıdaki notta ve listede)
    inviteCreated: 'Davet oluşturuldu',
    inviteFailed: 'Davet oluşturulamadı. Bilgileri kontrol edip yeniden deneyin.',
    /** Davet oluşturulduğu andaki posta durumu notu (bildirimde) */
    inviteMailNote: {
      queued: 'İleti gönderim kuyruğunda; durumu kullanıcı listesinden izleyin.',
      provider_accepted: 'Posta sağlayıcısı iletiyi kabul etti.',
      failed: 'İleti gönderilemedi.',
      not_configured: 'Sistem e-postası yapılandırılmamış: ileti gönderilmedi.',
      uncertain: 'İletinin sonucu belirsiz; yeniden gönderilmedi.',
      sink: 'Sınama kipi: ileti gerçek alıcıya gönderilmez.',
      held: 'İleti inceleme bekliyor; henüz gönderilmedi.',
    },
    inviteMailLabel: 'Davet postası',
    /** Kullanıcı listesinde davet iletisinin durumu ("sağlayıcı kabul etti" alıcıya teslim demek değildir) */
    inviteMailStatus: {
      queued: 'Kuyrukta',
      provider_accepted: 'Sağlayıcı kabul etti',
      failed: 'Gönderilemedi',
      not_configured: 'Posta yapılandırılmamış',
      uncertain: 'Sonucu belirsiz',
      sink: 'Sınama alıcısı',
      held: 'Beklemede',
    },
    inviteMailHint: {
      queued: 'İleti henüz posta sağlayıcısına verilmedi.',
      provider_accepted:
        'Posta sağlayıcısı iletiyi kabul etti. Alıcının gelen kutusuna ulaştığı bu ekrandan izlenmez.',
      failed:
        'Posta sağlayıcısı iletiyi reddetti ya da sağlayıcıya ulaşılamadı; ileti gönderilmedi.',
      not_configured: 'Sistem e-postası yapılandırılmadığı için ileti gönderilmedi.',
      uncertain:
        'İleti aktarıldıktan sonra sağlayıcıdan yanıt alınamadı; alıcıya ulaşmış olabilir. Otomatik olarak yeniden gönderilmez.',
      sink: 'Sınama kipinde iletiler gerçek alıcıya gönderilmez.',
      held: 'İleti inceleme bekliyor; henüz gönderilmedi.',
    },
    // M7 T2: daveti yeniden gönder — yöneticinin açık eylemi (otomatik yeniden gönderim yoktur)
    inviteResend: 'Daveti yeniden gönder',
    inviteResendTitle: (name: string) => `${name} için davet yeniden gönderilsin mi?`,
    inviteResendBody:
      'Yeni bir davet bağlantısı oluşturulur ve yeni bir ileti gönderim kuyruğuna alınır. Önceki davet bağlantısı geçersiz olur.',
    inviteResendUncertain:
      'Önceki iletinin sonucu belirsiz: alıcıya ulaşmış olabilir. Yeniden gönderirseniz alıcı iki ileti alabilir; yalnız son iletideki bağlantı çalışır.',
    inviteResendConfirm: 'Yeniden gönder',
    inviteResent: 'Davet yenilendi',
    inviteResendFailed: 'Davet yeniden gönderilemedi. Biraz sonra yeniden deneyin.',
    inviteResendRateLimited: 'Bu davet kısa sürede çok kez yenilendi. Daha sonra yeniden deneyin.',
    inviteResendAccepted: 'Kullanıcı daveti kabul etmiş; davet yeniden gönderilmedi.',
    inviteResendForbidden:
      'Kendinizde olmayan yetkileri taşıyan bir rolün davetini yeniden gönderemezsiniz.',
    // M7: rol değiştirme, askıya alma ve yeniden etkinleştirme — yöneticinin açık eylemi; onay penceresi etkiyi anlatır
    roleFor: (name: string) => `Rol: ${name}`,
    lockedOwner: 'Çalışma alanı sahibinin rolü ve durumu değiştirilemez.',
    lockedSelf: 'Kendi rolünüzü ve durumunuzu değiştiremezsiniz.',
    roleChangeTitle: (name: string) => `${name} için rol değiştirilsin mi?`,
    roleChangeBody: (from: string, to: string) =>
      `Rol “${from}” yerine “${to}” olur. Kullanıcının açık oturumları kapatılır; yeniden giriş yaptığında yeni rolün yetkileriyle çalışır.`,
    roleChangeConfirm: 'Rolü değiştir',
    roleChanged: (name: string, role: string) => `${name} artık “${role}” rolünde`,
    // H2: hassas yetki ekleyen rol değişikliği iki adımlı onaydan geçer
    roleSensitive: (names: string) =>
      `Bu rol hassas yetki ekliyor: ${names}. Değişiklik doğrudan uygulanmaz; onaya gönderilir ve sizden başka uygun bir yetkili onaylayınca geçerli olur.`,
    roleReason: 'Gerekçe',
    roleReasonHint: 'Neden gerekli? Onaylayan kişi ve denetim kaydı için (10–500 karakter).',
    roleReasonShort: 'Gerekçe en az 10 karakter olmalı.',
    roleRequestConfirm: 'Onaya gönder',
    roleRequested: (name: string, role: string) =>
      `${name} için “${role}” rolü onaya gönderildi; onaylanınca geçerli olur`,
    inviteSensitiveOwnerOnly:
      'Hassas yetki taşıyan rollerle (Standart, Sistem yöneticisi) yalnız çalışma alanı sahibi davet edebilir. Daha dar rolle davet edin; gereken yetki için sonra talep açın.',
    inviteSensitiveHint:
      'Bu rol hassas yetki taşır (ör. kayıt silme, dışa aktarma); bu rolle yalnız çalışma alanı sahibi davet edebilir.',
    // K1: çalışma alanı sahibi ≠ platform işletmecisi; yönetici daveti yeniden kimlik doğrulamayla
    inviteAdminReauthHint:
      'Sistem yöneticisi bu çalışma alanında kullanıcıları, yetkileri ve ayarları yönetir; Castinflow platform yönetimi vermez ve kimseyi çalışma alanı sahibi yapmaz. Yönetici daveti çalışma alanı sahibinin işidir ve kimlik yeniden doğrulanarak yapılır.',
    inviteReauthPasswordHint:
      'Yönetici daveti için parolanız yeniden istenir; açık oturum tek başına yetmez.',
    inviteReauthFailed:
      'Kimlik doğrulanamadı: parolanızı ve (iki adımlı doğrulama açıksa) doğrulama kodunuzu denetleyip yeniden deneyin.',
    suspend: 'Askıya al',
    suspendTitle: (name: string) => `${name} askıya alınsın mı?`,
    suspendBody:
      'Kullanıcının açık oturumları hemen kapatılır ve yeniden giriş yapamaz. Hesabı silinmez: kayıtları, rolü ve departmanı korunur; dilediğinizde yeniden etkinleştirebilirsiniz.',
    suspendConfirm: 'Askıya al',
    suspended: (name: string) => `${name} askıya alındı`,
    reactivate: 'Yeniden etkinleştir',
    reactivateTitle: (name: string) => `${name} yeniden etkinleştirilsin mi?`,
    reactivateBody: (role: string) =>
      `Kullanıcı “${role}” rolüyle yeniden giriş yapabilir. Kapatılan oturumları geri gelmez; yeniden giriş yapması gerekir.`,
    reactivateConfirm: 'Yeniden etkinleştir',
    reactivated: (name: string) => `${name} yeniden etkinleştirildi`,
    // v3.1 A3 (K-M7-44.9): ayrılış, kayıt devri ve davet iptali
    leave: 'Ayrıldı yap',
    leaveTitle: (name: string) => `${name} ayrıldı olarak işaretlensin mi?`,
    leaveBody:
      'Kullanıcının açık oturumları hemen kapatılır ve kişisel API anahtarları iptal edilir; yeniden giriş yapamaz ve kullanıcı sayısına sayılmaz. Kayıtları, geçmiş yazarlığı ve denetim kayıtları korunur. Kayıtlarını, açık aktivitelerini, paylaşımlarını, bekleyen onaylarını, otomasyon kurallarını ve listelerini devir sihirbazıyla başka kullanıcılara devredebilirsiniz.',
    leaveOpenHandover: 'Ardından kayıt devri sihirbazını aç',
    leaveConfirm: 'Ayrıldı yap',
    left: (name: string) => `${name} ayrıldı olarak işaretlendi`,
    handover: 'Kayıtları devret',
    inviteCancel: 'Daveti iptal et',
    inviteCancelTitle: (name: string) => `${name} için davet iptal edilsin mi?`,
    inviteCancelBody:
      'Davet bağlantısı hemen geçersiz olur ve kullanıcı daveti kabul edemez. Aynı adresi daha sonra yeniden davet etmek için "Daveti yeniden gönder" eylemini kullanabilirsiniz.',
    inviteCancelConfirm: 'Daveti iptal et',
    inviteCancelled: (name: string) => `${name} için davet iptal edildi`,
    inviteCancelledBadge: 'Davet iptal edildi',
    /** Rol/durum değişikliği reddinin sabit metinleri (sunucunun `details.reason` kodu; sunucu iletisi gösterilmez) */
    userActionError: {
      owner_protected: 'Çalışma alanı sahibinin rolü ve durumu değiştirilemez.',
      self_change: 'Kendi rolünüzü ve durumunuzu değiştiremezsiniz.',
      target_outranks_actor: 'Sizden fazla yetkili bir kullanıcıyı yönetemezsiniz.',
      grant_exceeds_actor: 'Kendinizde olmayan yetkileri taşıyan bir rolü veremezsiniz.',
      invite_pending:
        'Davet bekleyen kullanıcı daveti kabul ederek etkinleşir; durumu buradan değiştirilemez.',
      invite_cancelled:
        'Daveti iptal edilmiş kullanıcı etkinleştirilemez; daveti yeniden gönderin.',
      invite_already_accepted: 'Kullanıcı daveti kabul etmiş; davet iptal edilemez.',
      user_not_invited: 'Kullanıcı davetli durumda değil; liste yenilendi.',
      request_pending: 'Bu kullanıcı için onay bekleyen bir yetki talebi var.',
      capability_exceptions_changed: 'Kullanıcının yetkileri bu arada değişti; liste yenilendi.',
      sensitive_approval_required:
        'Bu rol hassas yetki ekliyor; değişiklik onaya gönderilmeli. Liste yenilendi, yeniden deneyin.',
      notFound: 'Kullanıcı bulunamadı; liste yenilendi.',
      forbidden: 'Bu işlem için yetkiniz yok.',
      failed: 'İşlem tamamlanamadı. Biraz sonra yeniden deneyin.',
    },
  },

  settings: {
    title: 'Ayarlar',
    description: 'Modül ayarları, bilgi giriş alanları ve haber akışı tercihleri.',
    modulesNav: 'Modüller',
    tabs: 'Ayar bölümleri',
    tabSettings: 'Ayarlar',
    tabFields: 'Bilgi giriş alanları',
    tabFeed: 'Haber akışı',
    tabLookups: 'Seçenek listeleri',
    version: (n: number) => `Yapılandırma sürümü ${n}`,
    publishToFeed: 'Haber akışında yayınla',
    requireOwner: 'Yetki sahibi zorunlu',
    forwardOnly: 'Yalnız ileri aşamaya geçilebilir',
    referencePrefix: 'Referans öneki',
    referencePrefixHint: 'Kayıt numarasının önüne eklenir (ör. SIP-).',
    save: 'Kaydet',
    saved: 'Ayarlar kaydedildi',
    saveFailed: 'Ayarlar kaydedilemedi. Yeniden deneyin.',
    readOnly: 'Ayarları değiştirme yetkiniz yok; değerleri yalnız görüntüleyebilirsiniz.',
    fieldColumn: 'Alan',
    visibleColumn: 'Göster',
    requiredColumn: 'Zorunlu',
    fieldsTable: 'Bilgi giriş alanları',
    fieldsEmptyTitle: 'Bu modülün alan kataloğu henüz tanımlanmadı',
    fieldsEmptyBody: 'Alanlar modül ekranıyla birlikte eklenecek.',
    resetFields: 'Varsayılana dön',
    fieldsSaved: 'Alan ayarları kaydedildi',
    lockedField: 'Kapatılamaz',
    noteTags: 'Not etiketleri',
    manageNoteTags: 'Not etiketlerini yönet',
    publishState: 'Yayın',
    feedVisibility: 'Akışta görünürlük',
    on: 'Açık',
    off: 'Kapalı',
    visible: 'Görünür',
    hidden: 'Gizli',
    none: 'Tanımlı değil',
    visibleFor: (label: string) => `Göster: ${label}`,
    requiredFor: (label: string) => `Zorunlu: ${label}`,
  },

  recordExtras: {
    tags: 'Etiketler',
    addTag: 'Etiket ekle',
    addTagPlaceholder: 'Etiket ara veya yaz',
    createTag: (name: string) => `“${name}” etiketini oluştur`,
    noTags: 'Etiket yok',
    noMatchingTag: 'Eşleşen etiket yok',
    favorite: 'Favori',
    reminderStatus: {
      scheduled: 'Planlandı',
      triggered: 'Zamanı geldi',
      completed: 'Tamamlandı',
      dismissed: 'Kapatıldı',
      cancelled: 'İptal edildi',
    },
    reminderDue: 'Zaman',
    reminderSnoozed: (n: number) => `${n} kez ertelendi`,
    complete: 'Tamamla',
    snooze: 'Ertele',
    dismiss: 'Kapat',
    reminderFormTitle: 'Hatırlatıcı ekle',
    reminderTitle: 'Başlık',
    reminderNote: 'Not',
    reminderWhen: 'Tarih ve saat',
    reminderTimeZone: 'Saat dilimi',
    reminderTimeZoneHint: 'Hatırlatıcı bu saat dilimindeki yerel saatte tetiklenir.',
    reminderAmbiguous:
      'Bu saat, yaz saati dönüşünde iki kez yaşanır; hatırlatıcı ilk anda tetiklenecek.',
    reminderWhenRequired: 'Tarih ve saat seçin',
    reminderSave: 'Hatırlatıcıyı kaydet',
    fileStatus: {
      clean: 'Tarandı',
      scanning: 'Tarama bekleniyor',
      scanDelayed: 'Tarama gecikti',
      scanFailed: 'Tarama tamamlanamadı',
      unscannableEncrypted: 'Taranamadı: şifreli',
      unscannableLimits: 'Taranamadı: tarama sınırı',
      infected: 'Reddedildi',
      rejected: 'Reddedildi',
      pending: 'Yükleme tamamlanmadı',
      uploaded: 'Doğrulanıyor',
      expired: 'Yükleme süresi doldu',
      deleted: 'Kaldırıldı',
      unavailable: 'İndirilemez',
    },
    fileExplain: {
      scanning:
        'Güvenlik taraması tamamlanana kadar dosya indirilemez. Tarama sonucu gelince indirme açılır.',
      scanDelayed:
        'Tarama hizmetine ulaşılamadığı için güvenlik taraması gecikti. Sistem kendiliğinden yeniden dener; tarama tamamlanana kadar dosya indirilemez.',
      scanFailed:
        'Güvenlik taraması tamamlanamadı; dosya indirilemez. Dosyayı yeniden yükleyebilirsiniz.',
      unscannableEncrypted:
        'Dosya parola korumalı ya da şifreli olduğu için içeriği taranamadı ve indirilemez. Şifresiz sürümünü yükleyin.',
      unscannableLimits:
        'Dosyanın içeriği güvenlik taramasının sınırlarını aştığı (çok büyük ya da iç içe arşiv) için tam taranamadı ve indirilemez. Daha küçük parçalar hâlinde ya da arşivden çıkararak yükleyin.',
      infected: 'Güvenlik taraması dosyada zararlı içerik buldu. Dosya indirilemez.',
      rejected: 'Dosya doğrulamayı geçemedi ve indirilemez.',
      pending: 'Dosya içeriği henüz gönderilmedi.',
      uploaded: 'Dosya alındı; boyut ve içerik doğrulanıyor.',
      expired: 'Dosya içeriği süresi içinde gönderilmedi. Dosyayı yeniden yükleyin.',
      deleted: 'Bu dosya kayıttan kaldırıldı.',
      unavailable: 'Dosya şu anda indirilemez.',
    },
    download: (name: string) => `İndir: ${name}`,
    downloadShort: 'İndir',
    removeFile: (name: string) => `Kaldır: ${name}`,
    removeShort: 'Kaldır',
    uploadedAt: (date: string) => `Yüklendi: ${date}`,
    uploadPhase: {
      uploading: (p: number) => `Yükleniyor (%${p})`,
      verifying: 'Doğrulanıyor',
      done: 'Yüklendi; güvenlik taraması bekleniyor',
      failed: 'Yükleme tamamlanamadı',
    },
    uploadProgress: (name: string) => `${name} yükleniyor`,
    retryUpload: 'Yeniden yükle',
  },

  // v3.1 B3: hesap durumu (deneme uyarısı, deneme sonu, askı) — ADR-0015 § 5
  account: {
    trialWarningTitle: 'Deneme süreniz bitiyor',
    trialWarning: (days: number, endsAt: string) =>
      `Deneme sürenizin bitmesine ${String(days)} gün kaldı (bitiş: ${endsAt}). Kesintisiz devam etmek için Castinflow ile iletişime geçin.`,
    trialEnded: (endsAt: string) => `Deneme bitişi: ${endsAt}.`,
    suspendedLead:
      'Bu çalışma alanında giriş, kayıt işlemleri ve dış gönderimler durduruldu. Verileriniz silinmedi.',
    logout: 'Çıkış yap',
  },

  status: {
    title: 'Sistem durumu',
    description: 'Uygulama sunucusunun ve bağımlı servislerin anlık durumu.',
    api: 'Uygulama sunucusu',
    readiness: 'Hazırlık',
    checking: 'Kontrol ediliyor',
    unreachable: 'Ulaşılamıyor',
    running: (v: string) => `Çalışıyor (sürüm ${v})`,
    ready: 'Hazır',
    notReady: 'Hazır değil',
    checks: 'Servis kontrolleri',
    colService: 'Servis',
    colResult: 'Sonuç',
    colDuration: 'Süre',
    ok: 'Tamam',
    failed: 'Sorunlu',
    ms: (n: number) => `${n} ms`,
    /** Hazırlık denetimi adları (sunucu kodu ikincil olarak kalır); bilinmeyen ad yalnız kodla gösterilir */
    serviceNames: {
      db: 'Veritabanı',
      schema: 'Veritabanı şeması',
      redis: 'Önbellek ve iş kuyruğu',
      storage: 'Dosya deposu',
    } as Readonly<Record<string, string>>,
  },
} as const;

/** Kaynak (Türkçe) sözlüğün biçimi: diğer dil paketleri bunun kısmi örtüsüdür (i18n/language.ts). */
export type Dictionary = typeof trBase;
/** Kaynak sözlük (çeviri kapsamı ölçümü ve testler için); arayüz `tr` üzerinden okur. */
export const trSource: Dictionary = trBase;

/**
 * Etkin sözlük (M6 WP 6.2): çalışma alanının arayüz dili Türkçe değilse o dilin örtüsü uygulanır; örtüde olmayan her metin
 * Türkçe kalır. Ad `tr` olarak korunur (yüzlerce kullanım yeri) — "etkin arayüz metinleri" anlamındadır.
 */
export const tr: Dictionary = storedUiLanguage() === 'en' ? mergeDictionary(trBase, en) : trBase;
